/**
 * Release 3 EXP-1 (lane C) — the client side of an on-demand export. `downloadVersion` with a transform answers 200
 * (cached → save the url now) or 202 (`export_render` job → one toast tracks it through `getJob` until `result.url`
 * is ready). Share guests go through `publicDownload` + the public jobs route with the same toast.
 */
import { api, req } from '@/lib/api/client';
import { isApiError } from '@/lib/api/errors';
import { JobFailedError, pollJob, saveUrl, type PollJobOptions } from '@/lib/api/jobs';
import type { Job } from '@/lib/api/types';
import type { ExportVersionResult } from '@/lib/api/queries';
import { dismissToast, toast, updateToast } from '@/stores/ui';

const PROGRESS_TOAST_MS = 60 * 60_000;
const READY_TOAST_MS = 60_000;

export type ExportFlowOptions = { filename?: string | null; signal?: AbortSignal; poll?: PollJobOptions };

/** Poll an export job with a progress toast; saves the rendition when it is done. Resolves with the job (or null when aborted / failed). */
export async function trackExportJob(fetchJob: () => Promise<Job>, opts: ExportFlowOptions = {}): Promise<Job | null> {
  const what = opts.filename ? opts.filename : 'your export';
  const id = toast({ kind: 'info', title: 'Rendering export…', body: `Preparing ${what}. This usually takes a few seconds.`, durationMs: PROGRESS_TOAST_MS });
  try {
    const job = await pollJob(fetchJob, {
      initialDelayMs: 600,
      maxDelayMs: 3000,
      ...opts.poll,
      signal: opts.signal,
      onProgress: (j) => updateToast(id, { title: j.state === 'running' ? `Rendering… ${Math.round(j.progress)}%` : 'Rendering export…', body: j.state === 'queued' ? 'Waiting for a worker.' : `Preparing ${what}.` }),
    });
    dismissToast(id);
    const url = job.result?.url ?? null;
    if (!url) {
      toast({ kind: 'error', title: 'Export finished without a download link' });
      return job;
    }
    const name = job.result?.filename ?? opts.filename ?? undefined;
    saveUrl(url, name);
    toast({ kind: 'success', title: 'Export ready', body: name ? `${name} · download started` : 'Download started', durationMs: READY_TOAST_MS, action: { label: 'Download again', onClick: () => saveUrl(url, name) } });
    return job;
  } catch (e) {
    dismissToast(id);
    if (e instanceof DOMException && e.name === 'AbortError') return null;
    toast({ kind: 'error', title: 'Export failed', body: e instanceof JobFailedError ? e.message : isApiError(e) ? e.message : e instanceof Error ? e.message : undefined });
    return null;
  }
}

/** Member flow: handle the `useExportVersion` result (200 → save now, 202 → track the job). */
export async function completeExport(result: ExportVersionResult, opts: ExportFlowOptions = {}): Promise<void> {
  if (result.kind === 'url') {
    const url = result.download.url;
    if (!url) return void toast({ kind: 'error', title: 'No download link was returned' });
    const name = result.download.filename ?? opts.filename ?? undefined;
    saveUrl(url, name);
    toast({ kind: 'success', title: 'Export ready', body: name ? `${name} · download started` : 'Download started', durationMs: 8000 });
    return;
  }
  const jobId = result.job.id;
  await trackExportJob(() => req(api.GET('/jobs/{jobId}', { params: { path: { jobId } } })) as Promise<Job>, { ...opts, filename: opts.filename ?? null });
}

/** Share flow: `publicDownload` with a preset on one asset → direct url or an `export_render` job on the public jobs route. */
export async function runPublicExport(token: string, assetId: string, presetId: string, opts: ExportFlowOptions = {}): Promise<void> {
  try {
    const d = (await req(api.POST('/public/share/{token}/download', { params: { path: { token } }, body: { asset_ids: [assetId], preset_id: presetId } }))) as { url?: string | null; filename?: string | null; job_id?: string | null };
    if (d.url) return saveUrl(d.url, d.filename ?? opts.filename ?? undefined);
    if (!d.job_id) return void toast({ kind: 'error', title: 'The server did not start the export' });
    const jobId = d.job_id;
    await trackExportJob(() => req(api.GET('/public/share/{token}/jobs/{jobId}', { params: { path: { token, jobId } } })) as Promise<Job>, opts);
  } catch (e) {
    toast({ kind: 'error', title: isApiError(e) ? e.message : 'Download failed' });
  }
}
